import React, { useEffect, useState } from 'react';

/**
 * IntroScreen — full-screen logo splash (like native app launch)
 *
 * Phase timeline:
 *   0ms       → logo starts (scale 0.75, opacity 0)
 *   0–500ms   → logo scales to 1.0 + fades in   [enter]
 *   500–1500ms→ logo visible, subtle breathe     [hold]
 *   1500–2000ms→ entire screen fades to 0        [exit]
 *   2000ms    → onComplete() called → unmount    [done]
 */
const IntroScreen = ({ theme, onComplete }) => {
  // 'enter' | 'hold' | 'exit'
  const [phase, setPhase] = useState('enter');

  useEffect(() => {
    const t1 = setTimeout(() => setPhase('hold'), 500);
    const t2 = setTimeout(() => setPhase('exit'), 1500);
    const t3 = setTimeout(() => onComplete && onComplete(), 2050);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [onComplete]);

  const isDark = theme === 'dark';

  const bgColor = isDark ? '#0d0f1a' : '#f1f5f9';
  const logoSrc = isDark ? '/averina-ai-app-icon.png' : '/averina-ai-icon.png';

  return (
    <div
      aria-hidden="true"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: bgColor,
        // Fade OUT the whole screen during exit phase
        opacity: phase === 'exit' ? 0 : 1,
        transition: phase === 'exit' ? 'opacity 0.5s ease-out' : 'none',
        pointerEvents: phase === 'exit' ? 'none' : 'all',
      }}
    >
      {/* Logo + tagline wrapper */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '20px',
          transform: phase === 'enter' ? 'scale(0.72)' : 'scale(1)',
          opacity:   phase === 'enter' ? 0 : 1,
          transition: phase === 'enter'
            ? 'transform 0.5s cubic-bezier(0.34,1.56,0.64,1), opacity 0.4s ease-out'
            : 'none',
          animation: phase === 'hold' ? 'introPulse 1.8s ease-in-out infinite' : 'none',
        }}
      >
        {/* Logo */}
        <img
          src={logoSrc}
          alt="Averina AI"
          style={{
            width: 'clamp(72px, 18vw, 120px)',
            height: 'clamp(72px, 18vw, 120px)',
            objectFit: 'contain',
            display: 'block',
          }}
        />

        {/* Powered by Averina AI */}
        <a
          href="https://www.averinaai.com/"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            fontSize: '12px',
            fontWeight: 500,
            letterSpacing: '0.04em',
            color: isDark ? 'rgba(148,163,184,0.8)' : 'rgba(100,116,139,0.9)',
            textDecoration: 'none',
            transition: 'color 0.2s',
            fontFamily: 'Inter, sans-serif',
          }}
          onMouseEnter={e => e.target.style.color = isDark ? '#e2e8f0' : '#1e40af'}
          onMouseLeave={e => e.target.style.color = isDark ? 'rgba(148,163,184,0.8)' : 'rgba(100,116,139,0.9)'}
        >
          Powered by <strong style={{ fontWeight: 700 }}>Averina AI</strong>
        </a>
      </div>
    </div>
  );
};

export default IntroScreen;
